/**
 * Un elemento de la lista de conversaciones.
 *
 * Se memoiza porque la lista se actualiza en cada evento SSE y solo cambia un
 * elemento cada vez.
 */
import { memo } from 'react';
import type { ReactElement } from 'react';
import type { ConversationSummary } from '@shared/api-types.ts';
import { CONVERSATION_STATUS_LABELS } from '@domain/types.ts';
import { formatDayLabel, formatTime, isSameDay, parseDate, truncate } from '../format.ts';
import { BOT_MODE_LABELS, conversationDisplayName } from '../state.ts';
import { Avatar } from './Avatar.tsx';

function listTimestamp(value: string | null): string {
  const date = parseDate(value);
  if (!date) return '';
  return isSameDay(date, new Date()) ? formatTime(value) : formatDayLabel(date);
}

const BOT_SHORT: Record<ConversationSummary['botMode'], string> = {
  auto: 'IA',
  paused: 'Persona',
  off: 'Sin IA',
};

const BOT_TONE: Record<ConversationSummary['botMode'], string> = {
  auto: 'bot--auto',
  paused: 'bot--paused',
  off: 'bot--off',
};

export const ConversationListItem = memo(function ConversationListItem({
  conversation,
  selected,
  currentUserId,
  onSelect,
}: {
  conversation: ConversationSummary;
  selected: boolean;
  currentUserId: string;
  onSelect: (id: string) => void;
}): ReactElement {
  const name = conversationDisplayName(conversation);
  const preview = conversation.lastMessagePreview
    ? `${conversation.lastMessageDirection === 'outbound' ? 'Tu: ' : ''}${conversation.lastMessagePreview}`
    : 'Sin mensajes todavia';
  const assigneeLabel = conversation.assignee
    ? conversation.assignee.id === currentUserId
      ? 'Asignada a ti'
      : conversation.assignee.name
    : 'Sin asignar';

  return (
    <button
      type="button"
      className={selected ? 'conv is-selected' : 'conv'}
      onClick={() => onSelect(conversation.id)}
      aria-current={selected ? 'true' : undefined}
    >
      <Avatar name={name} seed={conversation.contact.id} />

      <span className="conv__body">
        <span className="conv__row">
          <span className="conv__name">{name}</span>
          <span className="conv__time">{listTimestamp(conversation.lastMessageAt)}</span>
        </span>

        <span className="conv__row">
          <span className="conv__preview">{truncate(preview, 110)}</span>
          {conversation.unreadCount > 0 ? (
            <span className="badge" title={`${conversation.unreadCount} mensajes sin leer`}>
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          ) : null}
        </span>

        <span className="conv__row conv__row--meta">
          <span
            className={`statusdot statusdot--${conversation.status}`}
            title={CONVERSATION_STATUS_LABELS[conversation.status]}
          />
          <span className="conv__status">{CONVERSATION_STATUS_LABELS[conversation.status]}</span>
          <span className={`bot ${BOT_TONE[conversation.botMode]}`} title={BOT_MODE_LABELS[conversation.botMode]}>
            {BOT_SHORT[conversation.botMode]}
          </span>
          <span className={conversation.assignee ? 'conv__assignee' : 'conv__assignee conv__assignee--none'}>
            {assigneeLabel}
          </span>
        </span>

        {conversation.tags.length > 0 ? (
          <span className="conv__tags">
            {conversation.tags.slice(0, 3).map((tag) => (
              <span className="tag" key={tag}>
                {tag}
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </button>
  );
});
